// src/config/clipTable.ts — Klip adlarının TEK KAYNAĞI (ARCHITECTURE §5.1, ANIMATION §2.4).
// Kanonik ad = tam GLB klip adı (catalog.json animationSets, Rig_Medium). Türetilmiş ad sonekleri:
//   `~`        ayna (l↔r + quat (x,−y,−z,w) + pos x→−x)          — ClipDataSet'te AYRI veri
//   `@t0-t1`   dilim (kanonik klip sn; zaman ekseni KAYDIRILMAZ)  — ClipDataSet'te kanonik klibe alias
//   `#upper` / `#lower`  katman (track kümesi ayrımı)              — alias
//   `+`        additive (Hit_A+; referans = kendi 0. karesi)       — alias
// Kodda string literal klip adı YASAK: moves.ts / animMap.ts / clipLibrary.ts / build-clipdata.mjs yalnız CLIPS'i kullanır.
// Süreler catalog.json'dan (types.test çapraz doğrular); klip zamanları (clipStart/tS) hamlelerde moves.ts'de, durumlarda tuning ANIM'de.

/** GLB set kısaltmaları (ANIMATION başlık). */
export type ClipSet = 'CM' | 'CR' | 'GEN' | 'MB' | 'MA' | 'SIM' | 'SP' | 'TL';

const SET_DIR = '/assets/KayKit_Character_Animations_1.1/Animations/gltf/Rig_Medium/';
/** Set → GLB yolu (tarayıcı; public/assets symlink). `import.meta.env.BASE_URL` öneki view/assets.ts'de eklenir. */
export const SET_FILES: Readonly<Record<ClipSet, string>> = {
  CM: SET_DIR + 'Rig_Medium_CombatMelee.glb',        // 0.98 MB
  CR: SET_DIR + 'Rig_Medium_CombatRanged.glb',       // 1.01 MB — yalnız Ranged_Magic_Shoot (tembel)
  GEN: SET_DIR + 'Rig_Medium_General.glb',           // 0.83 MB
  MB: SET_DIR + 'Rig_Medium_MovementBasic.glb',      // 0.69 MB
  MA: SET_DIR + 'Rig_Medium_MovementAdvanced.glb',   // 0.72 MB
  SIM: SET_DIR + 'Rig_Medium_Simulation.glb',        // 0.86 MB
  SP: SET_DIR + 'Rig_Medium_Special.glb',            // 0.92 MB — yalnız Skeletons_Taunt (tembel)
  TL: SET_DIR + 'Rig_Medium_Tools.glb',              // 1.46 MB — yalnız Holding_A (tembel)
};
/** Yükleme ekranında zorunlu (4.1 MB) / maç başladıktan sonra arka planda (3.4 MB) — ARCHITECTURE §5.1, ANIMATION §8. */
export const REQUIRED_SETS: readonly ClipSet[] = ['CM', 'GEN', 'MB', 'MA', 'SIM'];
export const LAZY_SETS: readonly ClipSet[] = ['CR', 'SP', 'TL'];

// ─────────────────────────── Kanonik adlar (GLB) ───────────────────────────
const C = {
  // CombatMelee
  MELEE_UNARMED_IDLE: 'Melee_Unarmed_Idle', MELEE_2H_IDLE: 'Melee_2H_Idle',
  PUNCH_A: 'Melee_Unarmed_Attack_Punch_A', KICK: 'Melee_Unarmed_Attack_Kick',
  SPINNING: 'Melee_2H_Attack_Spinning', BLOCK_ATTACK: 'Melee_Block_Attack',
  MELEE_BLOCK: 'Melee_Block', BLOCKING: 'Melee_Blocking', BLOCK_HIT: 'Melee_Block_Hit',
  SLICE_H: 'Melee_1H_Attack_Slice_Horizontal', SLICE_D: 'Melee_1H_Attack_Slice_Diagonal', CHOP_1H: 'Melee_1H_Attack_Chop',
  STAB_1H: 'Melee_1H_Attack_Stab', JUMP_CHOP: 'Melee_1H_Attack_Jump_Chop',
  SLICE_2H: 'Melee_2H_Attack_Slice', CHOP_2H: 'Melee_2H_Attack_Chop', SPIN_2H: 'Melee_2H_Attack_Spin', STAB_2H: 'Melee_2H_Attack_Stab',
  DUAL_SLICE: 'Melee_Dualwield_Attack_Slice', DUAL_CHOP: 'Melee_Dualwield_Attack_Chop', DUAL_STAB: 'Melee_Dualwield_Attack_Stab',
  // CombatRanged
  MAGIC_SHOOT: 'Ranged_Magic_Shoot',
  // General
  DEATH_A: 'Death_A', DEATH_A_POSE: 'Death_A_Pose', DEATH_B: 'Death_B', DEATH_B_POSE: 'Death_B_Pose',
  HIT_A: 'Hit_A', HIT_B: 'Hit_B', IDLE_A: 'Idle_A', IDLE_B: 'Idle_B',
  INTERACT: 'Interact', PICKUP: 'PickUp', SPAWN_AIR: 'Spawn_Air', THROW: 'Throw',
  // MovementBasic
  JUMP_START: 'Jump_Start', JUMP_IDLE: 'Jump_Idle', JUMP_LAND: 'Jump_Land',
  RUNNING_A: 'Running_A', RUNNING_B: 'Running_B', WALKING_A: 'Walking_A',
  // MovementAdvanced
  CRAWLING: 'Crawling', DODGE_B: 'Dodge_Backward', DODGE_F: 'Dodge_Forward', DODGE_L: 'Dodge_Left', DODGE_R: 'Dodge_Right',
  STRAFE_L: 'Running_Strafe_Left', STRAFE_R: 'Running_Strafe_Right', WALKING_BACK: 'Walking_Backwards',
  // Simulation
  CHEERING: 'Cheering', LIE_DOWN: 'Lie_Down', LIE_IDLE: 'Lie_Idle', LIE_STANDUP: 'Lie_StandUp', WAVING: 'Waving',
  // Special / Tools (tembel)
  SKELETONS_TAUNT: 'Skeletons_Taunt', HOLDING_A: 'Holding_A',
} as const;

/** Kanonik klip bilgisi: set + GLB süresi (catalog.json; süre 0 pozlar kütüphanede 1/30 s'e çekilir). */
export interface ClipInfo { set: ClipSet; duration: number }
export const CLIP_INFO: Readonly<Record<string, ClipInfo>> = {
  [C.MELEE_UNARMED_IDLE]: { set: 'CM', duration: 1.067 }, [C.MELEE_2H_IDLE]: { set: 'CM', duration: 1.067 },
  [C.PUNCH_A]: { set: 'CM', duration: 1.167 }, [C.KICK]: { set: 'CM', duration: 0.933 },
  [C.SPINNING]: { set: 'CM', duration: 0.667 }, [C.BLOCK_ATTACK]: { set: 'CM', duration: 1.067 },
  [C.MELEE_BLOCK]: { set: 'CM', duration: 1.067 }, [C.BLOCKING]: { set: 'CM', duration: 1.067 }, [C.BLOCK_HIT]: { set: 'CM', duration: 1.067 },
  [C.SLICE_H]: { set: 'CM', duration: 1.367 }, [C.SLICE_D]: { set: 'CM', duration: 1.0 }, [C.CHOP_1H]: { set: 'CM', duration: 1.067 },
  [C.STAB_1H]: { set: 'CM', duration: 1.6 }, [C.JUMP_CHOP]: { set: 'CM', duration: 1.333 },
  [C.SLICE_2H]: { set: 'CM', duration: 1.1 }, [C.CHOP_2H]: { set: 'CM', duration: 1.633 }, [C.SPIN_2H]: { set: 'CM', duration: 2.4 }, [C.STAB_2H]: { set: 'CM', duration: 1.6 },
  [C.DUAL_SLICE]: { set: 'CM', duration: 1.167 }, [C.DUAL_CHOP]: { set: 'CM', duration: 1.267 }, [C.DUAL_STAB]: { set: 'CM', duration: 1.6 },
  [C.MAGIC_SHOOT]: { set: 'CR', duration: 0.933 },
  [C.DEATH_A]: { set: 'GEN', duration: 0.8 }, [C.DEATH_A_POSE]: { set: 'GEN', duration: 0 }, [C.DEATH_B]: { set: 'GEN', duration: 2.633 }, [C.DEATH_B_POSE]: { set: 'GEN', duration: 0 },
  [C.HIT_A]: { set: 'GEN', duration: 0.667 }, [C.HIT_B]: { set: 'GEN', duration: 0.867 }, [C.IDLE_A]: { set: 'GEN', duration: 1.067 }, [C.IDLE_B]: { set: 'GEN', duration: 2.133 },
  [C.INTERACT]: { set: 'GEN', duration: 1.3 }, [C.PICKUP]: { set: 'GEN', duration: 1.3 }, [C.SPAWN_AIR]: { set: 'GEN', duration: 1.3 }, [C.THROW]: { set: 'GEN', duration: 1.367 },
  [C.JUMP_START]: { set: 'MB', duration: 0.6 }, [C.JUMP_IDLE]: { set: 'MB', duration: 1.067 }, [C.JUMP_LAND]: { set: 'MB', duration: 0.667 },
  [C.RUNNING_A]: { set: 'MB', duration: 0.8 }, [C.RUNNING_B]: { set: 'MB', duration: 0.8 }, [C.WALKING_A]: { set: 'MB', duration: 1.067 },
  [C.CRAWLING]: { set: 'MA', duration: 1.067 }, [C.DODGE_B]: { set: 'MA', duration: 0.4 }, [C.DODGE_F]: { set: 'MA', duration: 0.4 },
  [C.DODGE_L]: { set: 'MA', duration: 0.4 }, [C.DODGE_R]: { set: 'MA', duration: 0.4 },
  [C.STRAFE_L]: { set: 'MA', duration: 0.8 }, [C.STRAFE_R]: { set: 'MA', duration: 0.8 }, [C.WALKING_BACK]: { set: 'MA', duration: 1.067 },
  [C.CHEERING]: { set: 'SIM', duration: 1.667 }, [C.LIE_DOWN]: { set: 'SIM', duration: 3.0 }, [C.LIE_IDLE]: { set: 'SIM', duration: 2.667 },
  [C.LIE_STANDUP]: { set: 'SIM', duration: 2.333 }, [C.WAVING]: { set: 'SIM', duration: 2.133 },
  [C.SKELETONS_TAUNT]: { set: 'SP', duration: 1.033 }, [C.HOLDING_A]: { set: 'TL', duration: 1.0 },
};

// ─────────────────────────── CLIPS: kanonik + türetilmiş adlar ───────────────────────────
/** Tüm klip sabitleri. Yorumda kullanım yeri (DESIGN/ANIMATION satırı). */
export const CLIPS = {
  ...C,
  // ── Ayna (`~`) — ANIMATION §2.4 ──
  PUNCH_A_M: `${C.PUNCH_A}~`,            // L2 Cross (sol yumruk)
  DUAL_SLICE_M: `${C.DUAL_SLICE}~`,      // Dual W3
  SLICE_2H_M: `${C.SLICE_2H}~`,          // Asa W2
  KICK_M: `${C.KICK}~`,                  // isteğe bağlı (sol bacak; ?anim doğrulaması)
  // ── Dilim (`@t0-t1`, kanonik sn; eksen kaydırmaz) ──
  KICK_FLY: `${C.KICK}@0.25-0.50`,               // A2 Flying Kick (pauseAt 0.50)
  DEATH_B_FACEPLANT: `${C.DEATH_B}@1.30-1.75`,   // A2 whiff → yüzüstü (13 t)
  INTERACT_LIFT: `${C.INTERACT}@0.65-1.30`,      // omuza alma (FState.Lifting, 26 t)
  PICKUP_GRIP: `${C.PICKUP}@0.15-0.70`,          // G3 aşama 1 (0–15 t)
  PICKUP_RAISE: `${C.PICKUP}@0.70-1.20`,         // G3 aşama 2 (16–37 t)
  PICKUP_ITEM: `${C.PICKUP}@0-0.65`,             // eşya alma (tS 2.2, 18 t; equip 14. t)
  JUMP_CHOP_DIVE: `${C.JUMP_CHOP}@0.55-1.33`,    // 1H WA-Y Dive Chop (landSyncTime 0.72)
  SPIN_2H_SLICE: `${C.SPIN_2H}@0.45-1.80`,       // 2H WH Spin (iki pencere)
  DUAL_STAB_DASH: `${C.DUAL_STAB}@0.53-1.10`,    // Dual WH aşama 2
  BLOCK_ENTER: `${C.MELEE_BLOCK}@0-0.33`,        // BlockEnter tS 1.5 (13 t)
  BLOCK_PARRY_TAP: `${C.MELEE_BLOCK}@0-0.20`,    // parry dokunuşu (0.20'de paused 6 t)
  BLOCK_PARRY_OK: `${C.MELEE_BLOCK}@0.20-0.33`,  // parry başarısı (pause 6 t)
  BLOCK_HIT_SHORT: `${C.BLOCK_HIT}@0-0.5`,       // bloklu isabet tS 2.0 (15 t); guard break = tam BLOCK_HIT tS 1.33
  JUMP_LAND_SHORT: `${C.JUMP_LAND}@0-0.45`,      // JumpLand (8 t'de loko keser)
  JUMP_START_DOUBLE: `${C.JUMP_START}@0.30-0.60`, // çift zıplama tS 1.2 (DESIGN §3)
  DEATH_A_LAUNCH: `${C.DEATH_A}@0-0.267`,        // Launch (pause @0.267, pitch)
  DEATH_A_KNOCKDOWN: `${C.DEATH_A}@0.37-0.55`,   // Knockdown çakılma tS 1.0 (11 t)
  LIE_STANDUP_GETUP: `${C.LIE_STANDUP}@0.70-2.33`, // GetUp tS 2.05 (48 t)
  LIE_STANDUP_QUICK: `${C.LIE_STANDUP}@1.60-2.33`, // hızlı kalk tS 1.6 (28 t); kalkış tekmesi aşama 1 tS 2.0 (22 t)
  LIE_DOWN_DROP: `${C.LIE_DOWN}@1.0-1.4`,        // Carry'den düşürülme tS 2.0 (0.2 s)
  WAVING_TAUNT: `${C.WAVING}@0-1.4`,             // taunt varyantı
  // ── Üst gövde (`#upper`, yalnız UPPER_ALLOWED) ──
  PUNCH_A_UPPER: `${C.PUNCH_A}#upper`,           // A1 hava jab, dual hava X
  SLICE_H_UPPER: `${C.SLICE_H}#upper`,           // 1H hava X
  SLICE_2H_UPPER: `${C.SLICE_2H}#upper`,         // 2H / asa hava X
  DUAL_CHOP_UPPER: `${C.DUAL_CHOP}#upper`,       // dual hava Y
  HOLDING_A_UPPER: `${C.HOLDING_A}#upper`,       // Carry üst katman (TL gelene kadar BLOCKING_UPPER)
  BLOCKING_UPPER: `${C.BLOCKING}#upper`,         // Carry yedeği
  // ── Alt gövde (`#lower`) ──
  JUMP_IDLE_LOWER: `${C.JUMP_IDLE}#lower`,       // hava X/Y alt katman
  IDLE_A_LOWER: `${C.IDLE_A}#lower`,             // Carry dururken (silahlı idle)
  MELEE_UNARMED_IDLE_LOWER: `${C.MELEE_UNARMED_IDLE}#lower`,
  WALKING_A_LOWER: `${C.WALKING_A}#lower`,
  RUNNING_A_LOWER: `${C.RUNNING_A}#lower`,
  // ── Additive (`+`) ──
  HIT_A_ADD: `${C.HIT_A}+`,                      // arkadan hafif darbe flinch (w 0.8, 0.20 s sönüm)
} as const;
export type ClipKey = keyof typeof CLIPS;
export type ClipName = (typeof CLIPS)[ClipKey];

/** Kütüphaneye alınacak tüm adlar (kanonik + türetilmiş); clipLibrary/build-clipdata bu listeden yürür. */
export const ALL_CLIP_NAMES: readonly string[] = Object.values(CLIPS);
/** Yalnız kanonik (GLB) adlar. */
export const CANONICAL_CLIP_NAMES: readonly string[] = Object.keys(CLIP_INFO);

/** `#upper` türevi üretilebilecek klipler (ANIMATION §4; hipsYawRange > 25° ölçülürse türev üretilmez, clipdata.test hata verir). */
export const UPPER_ALLOWED: ReadonlySet<string> = new Set([C.PUNCH_A, C.SLICE_H, C.SLICE_2H, C.DUAL_CHOP, C.HOLDING_A, C.BLOCKING]);
/** `#lower` türevi üretilecek klipler (Carry / hava alt katmanı). */
export const LOWER_CLIPS: ReadonlySet<string> = new Set([C.JUMP_IDLE, C.IDLE_A, C.MELEE_UNARMED_IDLE, C.WALKING_A, C.RUNNING_A]);
/** Tembel setler gelmeden kullanılacak yedekler (ARCHITECTURE §5.1): türetilmiş ad → yedek ad. */
export const LAZY_FALLBACK: Readonly<Record<string, string>> = {
  [C.MAGIC_SHOOT]: C.STAB_2H,
  [C.SKELETONS_TAUNT]: C.CHEERING,
  [CLIPS.HOLDING_A_UPPER]: CLIPS.BLOCKING_UPPER,
};

// ─────────────────────────── Ad çözümleme ───────────────────────────
export interface ParsedClip {
  base: string;                 // kanonik GLB adı
  mirror: boolean;              // `~`
  slice: [number, number] | null; // `@t0-t1` (kanonik sn)
  layer: 'upper' | 'lower' | null; // `#upper` / `#lower`
  additive: boolean;            // `+`
}
/** Türetilmiş adı parçalar (yükleme zamanı; sıcak yol değil). Sonek sırası: base ~ @t0-t1 #layer + */
export function parseClipName(name: string): ParsedClip {
  let s = name;
  const additive = s.endsWith('+');
  if (additive) s = s.slice(0, -1);
  let layer: 'upper' | 'lower' | null = null;
  const hash = s.indexOf('#');
  if (hash >= 0) {
    const l = s.slice(hash + 1);
    if (l !== 'upper' && l !== 'lower') throw new Error(`clip layer: ${name}`);
    layer = l; s = s.slice(0, hash);
  }
  let slice: [number, number] | null = null;
  const at = s.indexOf('@');
  if (at >= 0) {
    const [a, b] = s.slice(at + 1).split('-');
    const t0 = Number(a), t1 = Number(b);
    if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) throw new Error(`clip slice: ${name}`);
    slice = [t0, t1]; s = s.slice(0, at);
  }
  const mirror = s.endsWith('~');
  if (mirror) s = s.slice(0, -1);
  return { base: s, mirror, slice, layer, additive };
}
/** Kanonik (GLB) ad: tüm sonekler atılır. ClipDataSet alias çözümü bunu kullanır ('~' hariç: aynalı veri ayrı). */
export const canonicalOf = (name: string): string => parseClipName(name).base;
/** Klibin ait olduğu set (türetilmiş adlar kanonik klibin setine gider). Bilinmeyen ad → hata (string literal sızıntısı yakalanır). */
export function clipSetOf(name: string): ClipSet {
  const info = CLIP_INFO[canonicalOf(name)];
  if (!info) throw new Error(`unknown clip: ${name}`);
  return info.set;
}
/** Kanonik klip süresi (sn); türetilmişte: dilim → t1, diğerleri kanonik süre. */
export function clipDurationOf(name: string): number {
  const p = parseClipName(name);
  const info = CLIP_INFO[p.base];
  if (!info) throw new Error(`unknown clip: ${name}`);
  return p.slice ? p.slice[1] : info.duration;
}
export const isKnownClip = (name: string): boolean => CLIP_INFO[parseClipName(name).base] !== undefined;
